import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { z } from "zod";
import { Activity, Download, PhoneCall, ShieldAlert } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { normalizeUrgency, isAtLeast, URGENCY_META } from "@/lib/urgency";
import { PageHeader } from "@/components/layout/AppShell";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { StatTile } from "@/components/ui/StatTile";
import { LinkButton } from "@/components/ui/Button";
import { Alert, LoadingPanel, UrgencyPanel } from "@/components/ui/Feedback";
import { RichText } from "@/components/RichText";
import { formatDate } from "@/components/ui/DataTable";
import { METRICS, type MetricKey } from "./healthCheckConfig";
import { cn } from "@/lib/cn";

const resultSchema = z.looseObject({
  session_uuid: z.string(),
  status: z.string(),
  check_type: z.string(),
  overall_urgency: z.string().nullable().default(null),
  summary: z.string().nullable().default(null),
  started_at: z.string().nullable().default(null),
  completed_at: z.string().nullable().default(null),
  measurements: z
    .array(
      z.looseObject({
        id: z.number(),
        metric_type: z.string(),
        numeric_value: z.number().nullable().default(null),
        unit: z.string().nullable().default(null),
        source_type: z.string().nullable().default(null),
        is_abnormal: z.number().default(0),
        measured_at: z.string().nullable().default(null),
      }),
    )
    .default([]),
  symptoms: z
    .array(
      z.looseObject({
        id: z.number(),
        symptom_code: z.string(),
        symptom_name: z.string(),
        severity: z.string().nullable().default(null),
      }),
    )
    .default([]),
  assessment: z
    .looseObject({
      explanation: z.string().nullable().default(null),
      urgency_before_ai: z.string().nullable().default(null),
      urgency_after_ai: z.string().nullable().default(null),
      model: z.string().nullable().default(null),
      success: z.number().default(0),
      rule_engine_result_json: z.looseObject({}).nullable().default(null),
      ai_result_json: z.looseObject({}).nullable().default(null),
    })
    .nullable()
    .default(null),
});

const ruleResultSchema = z.looseObject({
  urgency: z.string().optional(),
  triggered_rules: z.array(z.looseObject({ message: z.string().optional() })).optional(),
  recommended_actions: z.array(z.string()).optional(),
  requires_emergency_action: z.boolean().optional(),
});

const aiResultSchema = z.looseObject({
  explanation: z.string().optional(),
  key_findings: z.array(z.string()).optional(),
  questions_for_doctor: z.array(z.string()).optional(),
  limitations: z.array(z.string()).optional(),
  next_steps: z.array(z.string()).optional(),
});

/**
 * One band of the report.
 *
 * The result used to be eight separate cards in a 3/2 column split. The left
 * column always outran the right, so the page ended in a large empty rectangle,
 * and eight borders made a single reading flow look like eight unrelated
 * widgets. These are sections of one card instead: divided by a rule, not by a
 * gap.
 */
function Section({
  eyebrow,
  title,
  action,
  children,
  className,
}: {
  eyebrow: string;
  title: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    // The divider is switched off by the caller, not by `first:`: sections
    // live in two column wrappers, and `first:` matches the first child of
    // each wrapper — which silently dropped the rule between the stacked
    // sections on mobile.
    <section className={cn("border-hairline border-t px-5 py-4", className)}>
      <div className="mb-2.5 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="eyebrow mb-0.5">{eyebrow}</p>
          <h2 className="text-base leading-tight font-bold">{title}</h2>
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      {children}
    </section>
  );
}

export function HealthCheckResult() {
  const { id } = useParams<{ id: string }>();

  const { data, isPending, error } = useQuery({
    queryKey: ["patient", "health-check", id],
    queryFn: ({ signal }) => api.get(`/api/health-check/${id}`, resultSchema, signal),
    enabled: Boolean(id),
  });

  if (isPending) {
    return (
      <>
        <PageHeader title="Hasil pemeriksaan" />
        <Card>
          <LoadingPanel label="Memuat hasil" />
        </Card>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <PageHeader title="Hasil pemeriksaan" />
        <Alert tone="danger" title="Gagal memuat hasil">
          {error instanceof ApiError ? error.message : "Pemeriksaan tidak ditemukan."}
        </Alert>
      </>
    );
  }

  const urgency = normalizeUrgency(data.overall_urgency);
  const rules = ruleResultSchema.safeParse(data.assessment?.rule_engine_result_json ?? {});
  const ai = aiResultSchema.safeParse(data.assessment?.ai_result_json ?? {});
  const triggered = rules.success ? (rules.data.triggered_rules ?? []) : [];
  const actions = rules.success ? (rules.data.recommended_actions ?? []) : [];
  const aiData = ai.success ? ai.data : null;

  const aiFailed = data.assessment && !data.assessment.success;
  const hasQuestions = (aiData?.questions_for_doctor?.length ?? 0) > 0;
  const hasNextSteps = actions.length > 0 || (aiData?.next_steps?.length ?? 0) > 0;

  // The backend stores the model's narrative in both `summary` and
  // `assessment.explanation`, and for most results they are byte-identical.
  // Rendering both printed the same paragraph twice — once in the panel, then
  // again under its own heading a few centimetres below.
  const flatten = (value?: string | null) => (value ?? "").replace(/\s+/g, " ").trim();
  const explanation = data.assessment?.explanation ?? null;
  const showExplanation = Boolean(explanation) && flatten(explanation) !== flatten(data.summary);
  const hasLimitations = (aiData?.limitations?.length ?? 0) > 0;
  const hasActions = hasQuestions || hasNextSteps;
  /** Whether anything is rendered above the list row, which owns the card's
   *  first divider. */
  const hasNarrative = triggered.length > 0 || showExplanation;

  return (
    <>
      <PageHeader
        eyebrow={`Pemeriksaan ${formatDate(data.completed_at ?? data.started_at, true)}`}
        title="Hasil pemeriksaan"
        actions={
          <a
            href={`/patient/health-check/report/${data.session_uuid}.pdf`}
            className="border-hairline-strong bg-surface hover:bg-surface-raised inline-flex h-10 items-center gap-2 rounded-(--radius-control) border px-5 text-sm font-semibold"
          >
            <Download className="size-4" />
            Unduh PDF
          </a>
        }
      />

      {/* The panel title is the short standing guidance, not `summary`:
          summary is a full paragraph from the model, and rendering it at
          heading size produced a wall of 21px text. */}
      <UrgencyPanel level={urgency} className="mb-4">
        {data.summary && <p>{data.summary}</p>}
        {/* Provenance sits here, once, rather than on whichever section
            happens to be showing the narrative. */}
        {data.assessment?.model && (
          <p className="text-content-muted mt-2 text-xs">
            Penjelasan dibuat oleh {data.assessment.model}
          </p>
        )}
        {isAtLeast(urgency, "critical") && (
          <a
            href="tel:119"
            className="bg-critical-500 mt-3 inline-flex h-10 items-center gap-2 rounded-(--radius-control) px-5 text-sm font-bold text-white"
          >
            <PhoneCall className="size-4" />
            Hubungi 119
          </a>
        )}
      </UrgencyPanel>

      {aiFailed && (
        <Alert tone="warning" title="Penjelasan AI tidak tersedia" className="mb-5">
          Tingkat urgensi di atas tetap valid — ditentukan oleh aturan klinis deterministik, bukan
          oleh AI. Hanya penjelasan naratifnya yang gagal dibuat.
        </Alert>
      )}

      {/* Measurements read as data, not as prose, so they sit in the same
          tile row the dashboard uses rather than inside a panel of their own. */}
      {data.measurements.length > 0 ? (
        <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {data.measurements.map((item) => {
            const spec = METRICS[item.metric_type as MetricKey];
            const abnormal = item.is_abnormal === 1;
            return (
              <StatTile
                key={item.id}
                label={spec?.label ?? item.metric_type}
                value={
                  <span className={cn(abnormal && "text-urgent-text")}>
                    {item.numeric_value ?? "—"}
                  </span>
                }
                unit={item.unit ?? spec?.unit}
                caption={abnormal ? "Di luar normal" : (item.source_type ?? "manual")}
                className={cn(abnormal && "border-urgent-500/40")}
              />
            );
          })}
        </div>
      ) : (
        <p className="text-content-muted mb-4 flex items-center gap-2 text-sm">
          <Activity aria-hidden className="size-4 shrink-0" />
          Tidak ada pengukuran — penilaian dibuat hanya dari gejala yang dilaporkan.
        </p>
      )}

      {data.symptoms.length > 0 && (
        <div className="mb-5 flex flex-wrap items-center gap-2">
          <span className="eyebrow mr-1">Gejala dilaporkan</span>
          {data.symptoms.map((symptom) => (
            <Badge key={symptom.id} tone={symptom.severity === "severe" ? "critical" : "neutral"}>
              {symptom.symptom_name}
            </Badge>
          ))}
        </div>
      )}

      <Card className="overflow-hidden">
        {triggered.length > 0 && (
          <Section
            eyebrow="Aturan klinis"
            title="Temuan yang memicu penilaian"
            className="border-t-0"
          >
            <p className="text-content-muted mb-2 text-sm">
              Ditentukan secara deterministik. AI tidak dapat menurunkan tingkat urgensi ini.
            </p>
            <ul className="space-y-2">
              {triggered.map((rule, index) => (
                <li key={index} className="flex gap-2.5 text-sm">
                  <span
                    aria-hidden
                    className={cn(
                      "mt-1.5 size-1.5 shrink-0 rounded-full",
                      URGENCY_META[urgency].dot,
                    )}
                  />
                  <span>{rule.message ?? "Temuan tanpa keterangan"}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {showExplanation && explanation && (
          <Section
            eyebrow="Penjelasan"
            title="Apa artinya hasil ini"
            className={cn(triggered.length === 0 && "border-t-0")}
          >
            {/* Capped measure: the card now spans the full page, and a
                paragraph 1300px wide is unreadable. */}
            <RichText text={explanation} className="max-w-[80ch] text-sm leading-relaxed" />
          </Section>
        )}

        {/* Split by purpose: what to do on the left, what this cannot tell you
            on the right. Grid cells stretch to the tallest sibling, so the two
            sides are grouped to come out near the same length instead of
            leaving half a row blank. */}
        {(hasActions || hasLimitations) && (
          <div className={cn("grid", hasActions && hasLimitations && "md:grid-cols-2")}>
            {hasActions && (
              <div className="min-w-0">
                {hasQuestions && (
                  <Section
                    eyebrow="Bawa saat konsultasi"
                    title="Pertanyaan untuk dokter"
                    className={cn(!hasNarrative && "border-t-0")}
                  >
                    <ol className="list-decimal space-y-1.5 pl-5 text-sm">
                      {aiData?.questions_for_doctor?.map((question, index) => (
                        <li key={index}>{question}</li>
                      ))}
                    </ol>
                  </Section>
                )}

                {hasNextSteps && (
                  <Section
                    eyebrow="Tindak lanjut"
                    title="Langkah berikutnya"
                    className={cn(!hasNarrative && !hasQuestions && "border-t-0")}
                  >
                    <ul className="list-disc space-y-1.5 pl-5 text-sm">
                      {[...actions, ...(aiData?.next_steps ?? [])].map((action, index) => (
                        <li key={index}>{action}</li>
                      ))}
                    </ul>
                  </Section>
                )}
              </div>
            )}

            {hasLimitations && (
              <div
                className={cn("min-w-0", hasActions && "md:border-hairline md:border-l")}
              >
                <Section
                  eyebrow="Keterbatasan"
                  title="Yang tidak dapat disimpulkan"
                  className={cn(
                    hasActions && "md:border-t-0",
                    !hasNarrative && !hasActions && "border-t-0",
                  )}
                >
                  <ul className="text-content-muted list-disc space-y-1.5 pl-5 text-sm">
                    {aiData?.limitations?.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </Section>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Footer, not a card: an action and two lines of fine print do not need
          a border around them. */}
      <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
        <LinkButton to="/patient/health-check" variant="primary">
          Pemeriksaan baru
        </LinkButton>
        <Link to="/patient/health-hub" className="text-accent text-sm font-semibold hover:underline">
          Lihat seluruh riwayat pemeriksaan
        </Link>
      </div>

      <p className="text-content-muted mt-3 flex gap-2 text-xs">
        <ShieldAlert aria-hidden className="mt-px size-3.5 shrink-0" />
        Hasil ini adalah screening awal, bukan diagnosis. Tekanan darah, saturasi, glukosa, dan suhu
        harus berasal dari alat yang sesuai.
      </p>
    </>
  );
}
